



























import { Component, Mixins } from "vue-property-decorator";
import {
  ClaioContentDrawableObjectMixin,
  DraggableMixin
} from "./mixins";
import { MovableImageItem } from "../../../../domain/document/items";
import { IDocumentImageSource, ImageKinds } from "../../../../domain/document";
import { Size } from "../../../../domain/drawing";
import { DragKinds } from "../../../dragging/DragKinds";
import RectangleDragHandles from "../../../dragging/RectangleDragHandles.vue";
import { IKeyboardEventHandleable, IObjectStyleReadable } from "..";
import { KeyCodeProcessor } from "./KeyCodeProcessor";
import movieIcon from "@/assets/icon_movie.png";
import { IConfiguration } from "../../../../domain/configuration";
import { MovableImageObjectStyleReader } from "../styleReaders";
import { DomainRegistry } from "../../../../domain";
import { TrimmingResult } from "../../../drawing/dialogs";

@Component({
  components: {
    RectangleDragHandles,
  },
})
export default class MovableImageObject extends Mixins(
  ClaioContentDrawableObjectMixin,
  DraggableMixin
) implements IKeyboardEventHandleable {
  editingSource: MovableImageItem;

  get editStyleReader(): IObjectStyleReadable {
    const configuration = this.$store.getters.configuration as IConfiguration;
    return new MovableImageObjectStyleReader(configuration.style.document.editor.objects.movableImage);
  }
  
  $refs!: {
    objectContainer: HTMLDivElement,
    image: HTMLImageElement,
  };

  data(): any {
    return {
      isImageLoaded: false,
    };
  }
  isImageLoaded: boolean;

  loadImageSize(): void {
    this.$nextTick(() => {
      this.isImageLoaded = true;
      if (!this.editingSource.isResizeRequired) { return; }
      // V1では貼り付け画像は縦横比率を維持したまま変形する仕様らしい
      this.writeProperty((s: MovableImageItem) => {
        s.geometry.rectangle.size = this.getLimitedImageSize(this.$refs.image);
        s.isResizeRequired = false;
      });
    });
  }

  private getLimitedImageSize(image: HTMLImageElement): Size {
    const naturalSize = this.getNaturalSize(image);
    const maxSize = this.getDropImageMaxSize();
    if (!maxSize) {
      return naturalSize;
    }
    const ratio = Math.min(maxSize.width / naturalSize.width, maxSize.height / naturalSize.height, 1);
    return naturalSize.multiply(ratio);
  }

  private getNaturalSize(image: HTMLImageElement): Size {
    return new Size(image.naturalWidth, image.naturalHeight);
  }

  private getDropImageMaxSize(): Size {
    const sizeLimitation = this.$configuration.behavior.document.editor.imageDropper.sizeLimitation;
    return sizeLimitation.isEnable
      ? new Size(sizeLimitation.maxSize.width, sizeLimitation.maxSize.height)
      : null;
  }

  onDoubleClicked(): void {
    this.launchClaioApplicationWhenNeeded();
  }

  onDragHandleSelected(event: PointerEvent, kind: DragKinds): void {
    this.emitFocus(); // focusが当たってないこともありえるのでfocus当てる。
    this.$nextTick(() => {
      this.draggableItemHandleSelected(
        event,
        kind,
        this.$refs.objectContainer,
        true
      );
    });
  }

  editImage(): void {
    this.editClaioWhenNeeded();
  }

  editImages(): void {
    this.editClaioImagesWhenNeeded();
  }

  get isErasable(): boolean {
    return (this.editingSource.imageKind === ImageKinds.Image);
  }

  async eraseImage(): Promise<void> {
    const result = await this.$eraseImage(this.getBoundingClientRectangle(), this.scale, this.$refs.image.src, this.$refs.image);
    if (!!result) {
      this.writeProperty((s: MovableImageItem) => {
        s.imageSource = result;
        // サイズが変更されていて原本画像と不一致となりClaioSchemaで正常に対応できないため、原本画像も上書きする
        s.originalImageSource = result.clone() as IDocumentImageSource;
        s.imageKind = ImageKinds.Image;
      });
    }
  }

  handleKeyboardEvent(event: KeyboardEvent): boolean {
    if (!this.isImageLoaded) {
      return false;
    }
    const moveOffset = KeyCodeProcessor.toMoveOffset(event.keyCode);
    if (moveOffset) {
      this.writeProperty(s => s.geometry.rectangle = s.geometry.rectangle.move(moveOffset));
      return true;
    }
    return false;
  }

  get isMovieImage(): boolean {
    return (this.editingSource.imageKind === ImageKinds.ClaioMovie);
  }

  get movieIconImageSource() {
    return movieIcon;
  }

  get maxSizeStyleProperties(): any {
    return {
      maxWidth: this.size.width + "px",
      maxHeight: this.size.height + "px",
    };
  }

  get isTrimmableImage(): boolean {
    // シェーマを画像切り抜きしてしまうと原本画像との整合がおかしくなるので切り抜きの対象外としている
    // それ以外の画像では切り抜きで原本画像も更新することで辻褄を合わせている
    return this.isImageLoaded && this.editingSource.isEditable && !this.editingSource.isFreeDrawn && (this.editingSource.imageKind !== ImageKinds.ClaioSchema) && (this.editingSource.imageKind !== ImageKinds.ClaioSchemaEdit);
  }
  
  async trimImage(): Promise<void> {
    if (!this.isTrimmableImage) {
      this.$logger.warn("画像切り抜き不可としている画像です。実装を確認してください。");
      return;
    }
    try {
      const result = await this.commonComponents.dialogs.trimming.show(this.imageSource);
      if (!result || !result.imageDataText) { return; }
      const imageSource = await this.createImageSourceFromImageDataText(result.imageDataText);
      const calculatedSize = this.calculateTrimmedImageObjectSize(this.editingSource.geometry.rectangle.size, result);
      this.writeProperty((s: MovableImageItem) => {
        // ClaioSchemaの挙動と辻褄を合わせるのが困難のため、原本画像も更新している
        // ClaioImageIdなどは維持したいのでclearImageInformation()は呼ばない
        s.replaceImageSource(imageSource);
        // 切り抜かれた画像サイズにあわせてオブジェクトのサイズ変更も必要
        s.geometry.rectangle.size = calculatedSize;
      });
    } catch (e) {
      this.$error("画像切り抜き", "処理中にエラーが発生しました。", e);
    }
  }

  private calculateTrimmedImageObjectSize(size: Size, result: TrimmingResult): Size {
    // 切り抜き前の画像と同じ表示倍率となるようにサイズを計算している
    const naturalSize = this.getNaturalSize(this.$refs.image);
    const ratio = size.height / naturalSize.height;
    return result.size.multiply(ratio);
  }

  private async createImageSourceFromImageDataText(imageDataText: string): Promise<IDocumentImageSource> {
    const documentRepository = DomainRegistry.document.getDocumentRepository();
    return documentRepository.createImageSource(imageDataText);
  }
}
